import { useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import React from 'react';
import { MutualFeedbackRemoval } from 'tradera/apps/tradera/components/profile/mutual-feedback-removal';
import { ReplyButton } from 'tradera/apps/tradera/components/profile/reply-button';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectHasTranslatedReview,
    selectTranslatedReview,
    selectTranslatedReviewsProviderName
} from 'tradera/state/views/profile/selectors';
import TranslateIcon from '@tradera/blueprint/theme/icons2/translate.svg?jsx';
import RatingNegativeIcon from '@tradera/blueprint/theme/icons2/rating-negative.svg?jsx';
import RatingPositiveIcon from '@tradera/blueprint/theme/icons2/rating-positive.svg?jsx';
import { TranslatedByGoogle } from 'tradera/components/translation/translated-by-google';
import { loadAndShowTranslatedReviews } from 'tradera/state/views/profile/actions';
import { resetTranslatedReview } from 'tradera/state/views/profile/reducer';
import {
    areLanguageCodesIso2Equal,
    getLanguageFromCountryCode
} from 'tradera/localization/languagecode';
import { selectPreferredLanguage } from 'tradera/state/language/selectors';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { Button } from 'tradera/components/atoms/button/button';

export const FeedbackItem = ({ feedbackItem }) => {
    const dispatch = useDispatch();
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const translatedReview = useSelector(
        selectTranslatedReview(feedbackItem.gradeId)
    );
    const hasTranslatedReview = useSelector(
        selectHasTranslatedReview(feedbackItem.gradeId)
    );
    const providerName = useSelector(selectTranslatedReviewsProviderName);

    const countryCodeSameAsFeedbackItemCountryCode = areLanguageCodesIso2Equal(
        useSelector(selectPreferredLanguage).languageCodeIso2,
        getLanguageFromCountryCode(
            feedbackItem?.feedbackMemberCountryCodeIso2,
            useIsFeatureEnabled('french-dutch-selectable')
        )
    );

    const hasTranslateableReview =
        !hasTranslatedReview && !countryCodeSameAsFeedbackItemCountryCode;

    const feedBackItemClicked = (e) => {
        e.preventDefault();
        dispatch(loadAndShowTranslatedReviews([feedbackItem.gradeId]));
    };

    const reset = () => {
        dispatch(resetTranslatedReview({ gradeId: feedbackItem.gradeId }));
    };

    const isNegative = feedbackItem.rating === 'Negative';

    return (
        <li>
            <article className="feedback-item media">
                <div className="media-object feedback-rating">
                    {isNegative ? (
                        <RatingNegativeIcon className="icon icon-md icon-danger" />
                    ) : (
                        <RatingPositiveIcon className="icon icon-md icon-success" />
                    )}
                </div>
                <div className="media-body">
                    <time
                        className="feedback-item-date"
                        dateTime={feedbackItem.createdDateTime}
                        title={feedbackItem.createdString}>
                        {feedbackItem.createdAgo}
                    </time>
                    <header className="feedback-item-header-react feedback-item-child-margins">
                        <strong>
                            <a
                                data-nav
                                href={toLocalizedUrl(
                                    `/profile/feedback/${feedbackItem.feedbackMemberId}/${feedbackItem.slugifiedFeedbackMemberAlias}`
                                )}>
                                {feedbackItem.feedbackMemberAlias}
                            </a>
                        </strong>
                        <span className="feedback-item-role">
                            ({feedbackItem.feedBackMemberRoleDescription})
                        </span>
                    </header>

                    {feedbackItem.hasText && !hasTranslatedReview && (
                        <p className="feedback-item-comment feedback-item-meta">
                            {feedbackItem.text}
                            {feedbackItem.reply && (
                                <p>
                                    <strong className="feedback-item-reply-title feedback-item-meta mb-0">
                                        {t('profileRating_comment')}:&nbsp;
                                    </strong>
                                    <span className="feedback-item-reply-text feedback-item-meta">
                                        {feedbackItem.reply}
                                    </span>
                                </p>
                            )}
                            {hasTranslateableReview && (
                                <div>
                                    <TranslateIcon className="icon mr-1" />
                                    <Button
                                        theme="link"
                                        onClick={feedBackItemClicked}>
                                        {t('profileRating_toLanguage')}
                                    </Button>
                                </div>
                            )}
                        </p>
                    )}

                    {feedbackItem.hasText && hasTranslatedReview && (
                        <p className="feedback-item-comment feedback-item-meta">
                            {translatedReview.text}
                            {translatedReview.reply && (
                                <p>
                                    <strong className="feedback-item-reply-title feedback-item-meta mb-0">
                                        {t('profileRating_comment')}:&nbsp;
                                    </strong>
                                    <span className="feedback-item-reply-text feedback-item-meta">
                                        {translatedReview.reply}
                                    </span>
                                </p>
                            )}
                            <div className="d-flex align-items-center">
                                <TranslateIcon className="icon mr-1" />
                                <Button theme="link" onClick={reset}>
                                    {t('profileRating_showOriginal')}
                                </Button>
                                {providerName === 'Google Translate' && (
                                    <TranslatedByGoogle />
                                )}
                            </div>
                        </p>
                    )}

                    {feedbackItem.hasReply && !feedbackItem.hasText && (
                        <>
                            <h5 className="feedback-item-reply-title feedback-item-meta mb-0">
                                {t('profileRating_comment')}:&nbsp;
                            </h5>
                            <p className="feedback-item-reply-text feedback-item-meta">
                                {feedbackItem.reply}
                            </p>
                        </>
                    )}
                    <p className="feedback-item-description feedback-item-meta mb-0">
                        {feedbackItem.hasPassed120Days ? (
                            <a href={feedbackItem.canonicalUrl} data-nav>
                                {`${feedbackItem.itemId} - ${feedbackItem.itemDescription}`}
                            </a>
                        ) : (
                            feedbackItem.itemId
                        )}
                    </p>
                    <div className="feedback-item-action-buttons float-right">
                        {feedbackItem.mutualFeedbackRemovalItem && (
                            <MutualFeedbackRemoval
                                mutualFeedbackRemovalItem={
                                    feedbackItem.mutualFeedbackRemovalItem
                                }
                            />
                        )}
                        {feedbackItem.isReplyAllowed && (
                            <ReplyButton feedbackItem={feedbackItem} />
                        )}
                    </div>
                </div>
            </article>
        </li>
    );
};
